/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import {
    DateFormats,
    formatDate,
    formatNameLastFirst,
    formatPhoneNumber,
} from '@strata/helper-functions';
import { ActionButtonText, Button } from '@strata/react-buttons';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import { FormSection } from '@strata/react-form-section';
import { type ColumnDef, Grid, PrimaryData, SecondaryData } from '@strata/react-grid';
import { PhoneInputFormik, TextInputFormik } from '@strata/react-inputs';
import { MessagingBlock } from '@strata/react-messaging-block';
import { PageTitle } from '@strata/react-page-title';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { validationStrings } from '@strata/strata-ui-strings';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { ArchivedIntakeSourceActionsMenu } from 'components/intakeSources/ArchivedIntakeSourceActionsMenu';
import { CustomTimeFrameOptions } from 'services/constants';
import { useAppDispatch, useAppSelector } from 'store';
import { type IntakeSourceSummary, useGetIntakeSourcesQuery } from 'store/api/intakeCaseApi';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridsThunks';
import {
    clearArchivedIntakeSourceSearch,
    type SearchOption,
    SearchOptions,
    setArchivedIntakeSourceSearch,
} from 'store/intakeSources/archivedIntakeSourceSlice';

const InitialSortProperties = [{ id: 'received_date', desc: true }];

const columns: ColumnDef<IntakeSourceSummary>[] = [
    {
        accessorKey: 'member_last_name',
        header: 'Patient',
        size: 150,
        cell: ({ getValue, row }) => (
            <PrimaryData data={formatNameLastFirst(getValue(), row.original.member_first_name)} />
        ),
    },
    {
        accessorKey: 'member_id',
        header: 'Member ID',
        size: 120,
    },
    {
        accessorKey: 'prescriber_last_name',
        header: 'Prescriber',
        size: 150,
        cell: ({ getValue, row }) => (
            <SecondaryData
                data={formatNameLastFirst(getValue(), row.original.prescriber_first_name)}
            />
        ),
    },
    {
        accessorKey: 'fax_number',
        header: 'Fax #',
        size: 120,
        cell: ({ getValue }) => <SecondaryData data={formatPhoneNumber(getValue())} />,
    },
    {
        accessorKey: 'received_date',
        header: 'Received Date',
        size: 150,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'archived_date',
        header: 'Date Archived',
        size: 150,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'intake_source_id',
        header: () => null,
        enableSorting: false,
        size: 48,
        cell: ({ getValue, row }) => (
            <ArchivedIntakeSourceActionsMenu
                intakeSourceId={getValue()}
                intakeSource={row.original}
            />
        ),
    },
];

export const ArchivedIntakeSourcesGrid = () => {
    const dispatch = useAppDispatch();

    const { searchParams } = useAppSelector((state) => state.archivedIntakeSource);

    const { sort, page, updateGridSort, updateCurrentPage } = useGridState(
        Grids.ArchivedIntakeSources,
        InitialSortProperties,
    );

    const apiQueryParams = {
        page,
        archived: true,
        timeframe: CustomTimeFrameOptions.ALL_TIME,
        memberId: searchParams?.memberId,
        memberFirstName: searchParams?.memberFirstName,
        memberLastName: searchParams?.memberLastName,
        prescriberNpi: searchParams?.prescriberNpi,
        intakeCaseId: searchParams?.intakeCaseId,
        faxNumber: searchParams?.faxNumber,
        sort,
    };
    const { data: firstPageData, isFetching: isFirstPageDataFetching } = useGetIntakeSourcesQuery(
        { ...apiQueryParams, page: 0 },
        { skip: !searchParams },
    );
    const { data: currentPageData, isFetching: isCurrentPageDataFetching } =
        useGetIntakeSourcesQuery(apiQueryParams, { skip: !searchParams });

    return (
        <FormPageContainer>
            <Formik
                initialValues={searchParams ?? { searchOption: SearchOptions.Member }}
                validationSchema={Yup.object().shape(
                    {
                        searchOption: Yup.string().required(
                            validationStrings.requiredField.errorMessage,
                        ),
                        memberId: Yup.string().when(
                            ['searchOption', 'memberFirstName', 'memberLastName'],
                            {
                                is: (
                                    searchOption: SearchOption,
                                    memberFirstName?: string,
                                    memberLastName?: string,
                                ) =>
                                    searchOption === SearchOptions.Member &&
                                    !(memberFirstName ?? memberLastName),
                                then: (schema) =>
                                    schema.required(validationStrings.requiredField.errorMessage),
                            },
                        ),
                        memberFirstName: Yup.string(),
                        memberLastName: Yup.string().when(['searchOption', 'memberId'], {
                            is: (searchOption: SearchOption, memberId?: string) =>
                                searchOption === SearchOptions.Member && !memberId,
                            then: (schema) =>
                                schema.required(validationStrings.requiredField.errorMessage),
                        }),
                        prescriberNpi: Yup.string()
                            .length(10, validationStrings.exactCharacter.lambda(10))
                            .when('searchOption', {
                                is: SearchOptions.Prescriber,
                                then: (schema) =>
                                    schema.required(validationStrings.requiredField.errorMessage),
                            }),
                        intakeCaseId: Yup.string().when('searchOption', {
                            is: SearchOptions.IntakeCase,
                            then: (schema) =>
                                schema.required(validationStrings.requiredField.errorMessage),
                        }),
                        faxNumber: PhoneInputFormik.validator().when('searchOption', {
                            is: SearchOptions.Fax,
                            then: (schema) =>
                                schema.required(validationStrings.requiredField.errorMessage),
                        }),
                    },
                    [['memberId', 'memberLastName']],
                )}
                onSubmit={(values) => {
                    dispatch(setArchivedIntakeSourceSearch({ searchParams: values }));
                }}
                enableReinitialize
                validateOnMount
            >
                {(formik) => (
                    <div
                        className="flex__gap--medium margin__around--medium"
                        style={{ flexDirection: 'column', height: '100%' }}
                    >
                        <PageTitle title="Archived Intake Sources" />
                        <FormSection
                            sectionTitle="Search"
                            content={
                                <Form>
                                    <RadioButtonGroupFormik
                                        name="searchOption"
                                        label="Search By"
                                        required
                                        inlineItems
                                        options={Object.values(SearchOptions)}
                                        onChange={(value) => {
                                            void formik.setValues({
                                                searchOption: value as SearchOption,
                                            });
                                        }}
                                    />
                                    <div className="flex__gap--medium">
                                        {(() => {
                                            switch (formik.values.searchOption) {
                                                case SearchOptions.Member:
                                                    return (
                                                        <>
                                                            <TextInputFormik
                                                                id="memberId"
                                                                label="Member ID"
                                                                required
                                                                onChange={(e) => {
                                                                    void formik.setValues({
                                                                        ...formik.values,
                                                                        memberId: e.target.value,
                                                                        memberFirstName: undefined,
                                                                        memberLastName: undefined,
                                                                    });
                                                                }}
                                                            />
                                                            <span className="margin__top--large">
                                                                or
                                                            </span>
                                                            <TextInputFormik
                                                                id="memberFirstName"
                                                                label="Patient First Name"
                                                                required
                                                                onChange={(e) => {
                                                                    void formik.setValues({
                                                                        ...formik.values,
                                                                        memberFirstName:
                                                                            e.target.value,
                                                                        memberId: undefined,
                                                                    });
                                                                }}
                                                            />
                                                            <TextInputFormik
                                                                id="memberLastName"
                                                                label="Patient Last Name"
                                                                required
                                                                onChange={(e) => {
                                                                    void formik.setValues({
                                                                        ...formik.values,
                                                                        memberLastName:
                                                                            e.target.value,
                                                                        memberId: undefined,
                                                                    });
                                                                }}
                                                            />
                                                        </>
                                                    );
                                                case SearchOptions.Prescriber:
                                                    return (
                                                        <TextInputFormik
                                                            id="prescriberNpi"
                                                            label="Prescriber NPI"
                                                            required
                                                        />
                                                    );
                                                case SearchOptions.IntakeCase:
                                                    return (
                                                        <TextInputFormik
                                                            id="intakeCaseId"
                                                            label="Intake Case ID"
                                                            required
                                                        />
                                                    );
                                                case SearchOptions.Fax:
                                                    return (
                                                        <PhoneInputFormik
                                                            id="faxNumber"
                                                            label="Fax #"
                                                            required
                                                        />
                                                    );
                                                default:
                                                    return null;
                                            }
                                        })()}

                                        <div
                                            className="flex__gap--medium"
                                            style={{
                                                flexGrow: 1,
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                            }}
                                        >
                                            <Button
                                                type="submit"
                                                id="search-archived-intake-sources-button"
                                                label="Search"
                                                disabled={
                                                    !(formik.dirty && formik.isValid) ||
                                                    formik.isSubmitting
                                                }
                                                intent={Button.Intent.PRIMARY}
                                            />
                                            <ActionButtonText
                                                style={{ height: 30 }}
                                                label="Clear Search"
                                                id="clear-archived-intake-sources-search-button"
                                                onClick={() =>
                                                    dispatch(clearArchivedIntakeSourceSearch())
                                                }
                                            />
                                        </div>
                                    </div>
                                </Form>
                            }
                        />
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1,
                                maxHeight: 'calc(100% - 270px)',
                            }}
                        >
                            <Grid
                                data={searchParams && currentPageData?.intakeSources}
                                isLoading={isFirstPageDataFetching || isCurrentPageDataFetching}
                                columns={columns}
                                manualSorting
                                sortProperties={sort}
                                onSort={updateGridSort}
                                currentPage={searchParams && page}
                                onPageChange={updateCurrentPage}
                                totalCount={firstPageData?.count}
                                noResults={
                                    <div className="sds_no-results">
                                        <MessagingBlock
                                            size={MessagingBlock.Sizes.MEDIUM}
                                            icon={IconNames.SEARCH}
                                            primaryText="No Information to show"
                                            secondaryText="Use the search above to find an intake source."
                                        />
                                    </div>
                                }
                            />
                        </div>
                    </div>
                )}
            </Formik>
        </FormPageContainer>
    );
};
